import React, { useContext, useState } from "react";
import type { EmailSchedule } from "@components/job_alerts/types";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@/sprout/flash";
import { useEffectAfterMount } from "@/hooks/use_effect_after_mount";
import { type Option, SingleSelect } from "@sprout/select";
import axios from "axios";

interface EmailSchedules {
  emailSchedule: EmailSchedule;
  emailSchedules: { [key: string]: number };
  notificationSchedulePath: string;
}

export const EmailSchedulesContext = React.createContext<EmailSchedules>(
  null as unknown as EmailSchedules,
);

interface Props {
  shortCadence?: boolean;
}

const Preferences = ({ shortCadence = false }: Props) => {
  const showFlash = useShowFlash();
  const { emailSchedule, emailSchedules, notificationSchedulePath } =
    useContext(EmailSchedulesContext);

  const { t } = useTranslation("jobAlerts");

  const getCadenceText = (cadence: string) => {
    return shortCadence
      ? cadence.charAt(0).toUpperCase() + cadence.slice(1)
      : t("preferences.sendEmailCadence", { cadence });
  };

  const scheduleOptions = Object.keys(emailSchedules).map((cadence) => ({
    value: emailSchedules[cadence],
    label: getCadenceText(cadence),
  }));

  const defaultEmailSchedule =
    scheduleOptions.find((option) => option.value === emailSchedule.id) || scheduleOptions[0];

  const [schedule, setSchedule] = useState<Option>(defaultEmailSchedule);

  const cadence =
    schedule.value === emailSchedules["daily"] ? t("preferences.daily") : t("preferences.weekly");

  useEffectAfterMount(() => {
    updateEmailSchedule();
  }, [schedule.value]);

  const updateEmailSchedule = async () => {
    try {
      await axios.patch(notificationSchedulePath, {
        cadence,
      });
      showFlash(t("preferences.frequencyUpdated"));
    } catch (e) {
      console.error("Error updating email schedule", e);
    }
  };

  return (
    <div className="preferences">
      <SingleSelect
        onChange={(value) => setSchedule(value as Option)}
        value={schedule}
        label=""
        options={scheduleOptions}
      />
    </div>
  );
};

export default Preferences;
